import { CommonEvents, Preferences } from '@3ds/common-preferences'

import type { MediaTypeConverter } from '~/entities/content/media/api/medias'

import { getSwymUrl } from '~/features-shared/config-manager/stores/swym-config'
import { proxifyUrl } from '~/features-shared/libs/utils/url-utils'

/** File download target options for broswer */
export enum Target {
  BLANK = '_blank',
  PARENT = '_parent',
  SELF = '_self',
  TOP = '_top',
}

/**
 * Read a file as text
 * @param file File to read
 * @returns file  content as a text string
 */
export function readFileAsText(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
    reader.readAsText(file)
  })
}
/**
 * Utility method to download file with given url
 * @param options options
 * @param options.fileUrl url which will download the file
 * @info url example '/api/community/getimportmembercsvsample/import-community-members-sample.csv'
 * @param options.extension extension of the file to be downloaded. (eg: 'csv' ; 'txt' ; etc...)
 * @param options.fileName Name of the file to be downloaded eg. 'import-community-members-sample.csv'
 * @param options.target _blank | _parent | _self | _top
 * @param options.isCompleteURL is the url absolute?
 */
export function downloadFileFromUrl({ fileUrl, extension, fileName, target, isCompleteURL }:
{
  fileUrl: string
  extension: string
  fileName: string
  target?: Target
  isCompleteURL?: boolean
}): void {
  const completeFileUrl = `${isCompleteURL ? '' : getSwymUrl()}${fileUrl}`
  const downloadUrl = proxifyUrl(completeFileUrl)

  // For native app - Need to send an events
  if (Preferences.isNativeApp()) {
    CommonEvents.emitEventToNativeApp(CommonEvents.EVENTS.NOTIFY_APP_DOWNLOAD_URL, {
      downloadUrl,
      extension,
      fileName,
    })
  }
  else {
    window.open(completeFileUrl, target)
  }
}

/**
 * Utility method to download file
 * @param file The file to be downloaded
 */
export function downloadFile(file: File | undefined) {
  if (!file)
    return

  const link = document.createElement('a')
  link.href = URL.createObjectURL(file)
  link.download = file?.name
  link.click()
  URL.revokeObjectURL(link.href)
}

/**
 * Utility method to convert extension type from list to string for limiting upload file type
 * @param extensions All allowed extensions
 * @returns string allowed extensions
 */
export function convertExtensionsListToAcceptFormat(extensions: MediaTypeConverter<string[]>): string {
  return Object.values(extensions)
    .flat()
    .map(ext => `.${ext}`)
    .sort()
    .join(', ')
}
